import { CButton, Dialog } from '@/shared/ui'
import style from './index.module.scss'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { setDialogs } from '@/store/sub_accounts'
import { useAppDispatch } from '@/store'
import { EDIT_DOCUMENTS } from '@/entities/cabinet'
import { toast } from 'react-toastify'
import { useState, useRef } from 'react'
import { updateUserDocuments } from '@/entities/admin'
import { SpinnerLoader } from '@/shared/ui/SpinnerLoader'

const DocumentsScheme = Yup.object().shape({
    license_file: Yup.mixed()
        .nullable()
        .test('file-type', 'License file must be a PDF file', function (value: any) {
            if (!value) return true
            return value.type === 'application/pdf'
        })
        .test('file-size', 'License file size must not exceed 20 MB', function (value: any) {
            if (!value) return true
            return value.size <= 20 * 1024 * 1024 // 20 MB
        }),
    cr_certificate_file: Yup.mixed()
        .nullable()
        .test('file-type', 'CR certificate file must be a PDF file', function (value: any) {
            if (!value) return true
            return value.type === 'application/pdf'
        })
        .test('file-size', 'CR certificate file size must not exceed 20 MB', function (value: any) {
            if (!value) return true
            return value.size <= 20 * 1024 * 1024 // 20 MB
        }),
}).test('at-least-one-file', 'At least one file must be provided', function (values) {
    return !!(values.license_file || values.cr_certificate_file)
})

export function EditDocumentsAlert({
    userId,
    onSuccess,
}: {
    userId: number
    onSuccess?: () => void
}) {
    const dispatch = useAppDispatch()
    const [loading, setLoading] = useState(false)
    const licenseFileRef = useRef<HTMLInputElement>(null)
    const crCertificateFileRef = useRef<HTMLInputElement>(null)

    const formik = useFormik({
        initialValues: {
            license_file: null as File | null,
            cr_certificate_file: null as File | null,
        },
        validationSchema: DocumentsScheme,
        validateOnChange: false,
        onSubmit: async (values, formikHelpers) => {
            // Validate at least one file is provided
            if (!values.license_file && !values.cr_certificate_file) {
                formikHelpers.setFieldError(
                    'license_file',
                    'At least one file (license_file or cr_certificate_file) must be provided.'
                )
                formikHelpers.setFieldError(
                    'cr_certificate_file',
                    'At least one file (license_file or cr_certificate_file) must be provided.'
                )
                return
            }

            setLoading(true)
            const formData = new FormData()
            formData.append('user_id', userId.toString())

            if (values.license_file) {
                formData.append('license_file', values.license_file)
            }
            if (values.cr_certificate_file) {
                formData.append('cr_certificate_file', values.cr_certificate_file)
            }

            try {
                await updateUserDocuments(formData)
                toast.success('Documents updated successfully')
                dispatch(setDialogs({ key: EDIT_DOCUMENTS, value: false }))
                if (onSuccess) {
                    onSuccess()
                }
                // Reset form
                formik.resetForm()
                if (licenseFileRef.current) {
                    licenseFileRef.current.value = ''
                }
                if (crCertificateFileRef.current) {
                    crCertificateFileRef.current.value = ''
                }
            } catch (error: any) {
                const errorMessage =
                    error?.response?.data?.message ||
                    error?.message ||
                    'Failed to update documents'
                toast.error(errorMessage)
            } finally {
                setLoading(false)
            }
        },
    })

    const handleFileChange = (
        field: 'license_file' | 'cr_certificate_file',
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0] || null
        formik.setFieldValue(field, file)
        formik.setFieldError(field, undefined)
    }

    return (
        <Dialog
            title={'Edit User Documents'}
            onClose={() => dispatch(setDialogs({ key: EDIT_DOCUMENTS, value: false }))}
            body={
                <div className={style.editWallet}>
                    <form
                        className={style.editWallet__form}
                        onSubmit={(e) => formik.handleSubmit(e)}
                    >
                        <div className={style.editWallet__form__fileInput}>
                            <label htmlFor="license_file">License File (PDF, max 20 MB)</label>
                            <input
                                ref={licenseFileRef}
                                id="license_file"
                                name="license_file"
                                type="file"
                                accept=".pdf,application/pdf"
                                onChange={(e) => handleFileChange('license_file', e)}
                            />
                            {formik.errors.license_file && (
                                <span className={style.editWallet__form__error}>
                                    {formik.errors.license_file}
                                </span>
                            )}
                            {formik.values.license_file && (
                                <span className={style.editWallet__form__fileInfo}>
                                    Selected: {formik.values.license_file.name} (
                                    {(formik.values.license_file.size / 1024 / 1024).toFixed(2)} MB)
                                </span>
                            )}
                        </div>

                        <div className={style.editWallet__form__fileInput}>
                            <label htmlFor="cr_certificate_file">
                                CR Certificate File (PDF, max 20 MB)
                            </label>
                            <input
                                ref={crCertificateFileRef}
                                id="cr_certificate_file"
                                name="cr_certificate_file"
                                type="file"
                                accept=".pdf,application/pdf"
                                onChange={(e) => handleFileChange('cr_certificate_file', e)}
                            />
                            {formik.errors.cr_certificate_file && (
                                <span className={style.editWallet__form__error}>
                                    {formik.errors.cr_certificate_file}
                                </span>
                            )}
                            {formik.values.cr_certificate_file && (
                                <span className={style.editWallet__form__fileInfo}>
                                    Selected: {formik.values.cr_certificate_file.name} (
                                    {(formik.values.cr_certificate_file.size / 1024 / 1024).toFixed(
                                        2
                                    )}{' '}
                                    MB)
                                </span>
                            )}
                        </div>

                        <div className={style.editWallet__form__warning}>
                            * At least one file must be provided. You can update only license, only
                            CR certificate, or both files simultaneously.
                        </div>

                        <div className={style.editWallet__form__error}>
                            {loading ? (
                                <SpinnerLoader />
                            ) : (
                                <CButton
                                    value={'Update Documents'}
                                    disabled={loading}
                                    type={'submit'}
                                    variant={'general'}
                                />
                            )}
                        </div>
                    </form>
                </div>
            }
        />
    )
}

